import {
  LayoutDashboard,
  Building2,
  CreditCard,
  Link2,
  Users,
  Mail,
  Settings,
  ShieldCheck,
  LogOut,
  Menu,
  X,
  ChevronDown,
} from 'lucide-react';
import { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import {
  requestSsoExchangeCode,
  buildSsoRedirectUrl,
  type SwitchableModule,
} from '@aia/platform-core';
import { AppSwitcher } from '@aia/ui-components';

import { NotificationBell } from '@/components/notifications/NotificationBell';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

const APP_URLS: Partial<Record<SwitchableModule, string | undefined>> = {
  pilot: import.meta.env.VITE_PILOT_URL,
  progress: import.meta.env.VITE_PROGRESS_URL,
  insight: import.meta.env.VITE_INSIGHT_URL,
  admin: import.meta.env.VITE_ADMIN_URL,
};

async function switchToApp(target: SwitchableModule): Promise<void> {
  const base = APP_URLS[target];
  if (!base) return;
  const code = await requestSsoExchangeCode(api, target);
  window.location.href = buildSsoRedirectUrl(base, code);
}

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Tenants', href: '/tenants', icon: Building2 },
  { name: 'Organizations', href: '/organizations', icon: Building2 },
  { name: 'Subscriptions', href: '/subscriptions', icon: CreditCard },
  { name: 'Payment Links', href: '/payment-links', icon: Link2 },
  { name: 'Users', href: '/users', icon: Users },
  { name: 'Email Templates', href: '/email-templates', icon: Mail },
  { name: 'Security', href: '/security', icon: ShieldCheck },
  { name: 'Settings', href: '/settings', icon: Settings },
];

export function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Close navigation menu"
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setSidebarOpen(false);
            }
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-aia-navy text-white transform transition-transform duration-200 ease-in-out lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex items-center gap-3 px-6 py-5 border-b border-white/10">
          <Link to="/dashboard" className="flex items-center gap-3">
            <img src="/brand/logo-color.png" alt="AIA" className="h-8 w-8 rounded" />
            <div>
              <p className="font-semibold text-sm">Platform Admin</p>
              <p className="text-xs text-gray-300">Academics in Action</p>
            </div>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="ml-auto lg:hidden p-1 rounded hover:bg-white/10"
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="px-3 py-4 space-y-1">
          {navigation.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  active
                    ? 'bg-white/15 text-white'
                    : 'text-gray-300 hover:bg-white/10 hover:text-white',
                )}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {item.name}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex items-center gap-4 border-b bg-white px-4 py-3 shadow-sm sm:px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 rounded-md hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-aia-blue"
            aria-label="Open sidebar"
          >
            <Menu className="h-5 w-5 text-gray-600" />
          </button>

          <div className="flex-1" />

          <AppSwitcher
            currentModule="admin"
            enabledModules={user?.enabledModules ?? []}
            isPlatformAdmin={user?.isPlatformAdmin}
            appUrls={APP_URLS}
            onSwitch={switchToApp}
          />

          <NotificationBell />

          {/* Profile dropdown */}
          <div className="relative">
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              className="flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-aia-blue"
              aria-expanded={profileOpen}
              aria-haspopup="true"
            >
              <div className="h-8 w-8 rounded-full bg-aia-navy flex items-center justify-center text-white text-sm font-medium">
                {user?.name?.[0]?.toUpperCase() || 'A'}
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500">Platform Admin</p>
              </div>
              <ChevronDown className="h-4 w-4 text-gray-500 hidden sm:block" />
            </button>

            {profileOpen && (
              <>
                <div
                  role="button"
                  tabIndex={0}
                  aria-label="Close profile menu"
                  className="fixed inset-0 z-40"
                  onClick={() => setProfileOpen(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setProfileOpen(false);
                    }
                  }}
                />
                <div className="absolute right-0 mt-1 w-56 bg-white border rounded-lg shadow-lg z-50 py-1">
                  <div className="px-4 py-3 border-b">
                    <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        {/* Page content */}
        <main id="main-content" className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
